import { SearchTreeInternalNode, SearchTreeNode } from '@/types/searchTreeTypes';
import { domainCodeAtom } from '@australiangreens/ag-internal-components';
import {
  Config,
  ImmutableTree,
  JsonGroup,
  JsonItem,
  JsonTree,
  Utils as QbUtils,
} from '@react-awesome-query-builder/mui';
import { usePrefetchQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useMapQbRulePropertiesFromSearchFilter } from './qbRulePropertiesMapping';
import useQueryBuilderConfig from './queryBuilderConfig';

// You can load query value from your backend storage (for saving see `Query.onChange()`)
const queryValue: JsonGroup = { id: QbUtils.uuid(), type: 'group', children1: [] };

export const useConvertTreeToJsonTree = () => {
  const mapQbRulePropertiesFromSearchFilter = useMapQbRulePropertiesFromSearchFilter();

  const convertNodeToJsonItem = async (tree: SearchTreeNode, not = false): Promise<JsonItem> => {
    if (tree.op === 'intersection') {
      const children = tree.nodes.map((node) => convertNodeToJsonItem(node));
      const internalNode = tree as SearchTreeInternalNode;
      return {
        type: 'group',
        id: internalNode._internalNodeId ?? QbUtils.uuid(),
        properties: { conjunction: 'AND', not },
        children1: await Promise.all(children),
      };
    }

    if (tree.op === 'union') {
      const children = tree.nodes.map((node) => convertNodeToJsonItem(node));
      const internalNode = tree as SearchTreeInternalNode;
      return {
        type: 'group',
        id: internalNode._internalNodeId ?? QbUtils.uuid(),
        properties: { conjunction: 'OR', not },
        children1: await Promise.all(children),
      };
    }

    if (tree.op === 'not') {
      if (tree.nodes.length > 1) throw Error('Wrong number of children for not');
      return convertNodeToJsonItem(tree.nodes[0], true);
    }

    if (tree.op === 'filter') {
      return {
        type: 'rule',
        id: tree._leafNodeId ?? QbUtils.uuid(),
        properties: await mapQbRulePropertiesFromSearchFilter(tree.filter),
      };
    }

    throw Error('Not supported');
  };
  return convertNodeToJsonItem;
};

export async function loadSearchTree(
  tree: SearchTreeNode | undefined,
  config: Config,
  convertTreeToJsonTree: ReturnType<typeof useConvertTreeToJsonTree>
): Promise<ImmutableTree> {
  if (!tree) return QbUtils.sanitizeTree(QbUtils.loadTree(queryValue), config).fixedTree;
  const loadedTree = await convertTreeToJsonTree(tree);
  return QbUtils.loadTree(loadedTree as JsonTree);
}

export const usePrefetchSearchTree = () => {
  const queryClient = useQueryClient();
  const config = useQueryBuilderConfig();
  const domainCode = useAtomValue(domainCodeAtom);
  const convertTreeToJsonTree = useConvertTreeToJsonTree();

  return (tree: SearchTreeNode | undefined) => {
    queryClient.prefetchQuery({
      queryKey: ['tree', domainCode, tree],
      queryFn: () => loadSearchTree(tree, config, convertTreeToJsonTree),
      staleTime: Infinity,
      gcTime: 2000,
    });
  };
};

export const usePrefetchDefaultSearchTree = () => {
  const config = useQueryBuilderConfig();
  const domainCode = useAtomValue(domainCodeAtom);
  const convertTreeToJsonTree = useConvertTreeToJsonTree();

  return usePrefetchQuery({
    queryKey: ['tree', domainCode, null],
    queryFn: () => loadSearchTree(undefined, config, convertTreeToJsonTree),
    staleTime: Infinity,
  });
};

export const useSearchTree = (initialTree: SearchTreeNode | undefined) => {
  const config = useQueryBuilderConfig();
  const convertTreeToJsonTree = useConvertTreeToJsonTree();
  const domainCode = useAtomValue(domainCodeAtom);

  return useQuery({
    queryKey: ['tree', domainCode, initialTree],
    queryFn: () => loadSearchTree(initialTree, config, convertTreeToJsonTree),
    staleTime: Infinity,
    gcTime: 2000,
    refetchOnWindowFocus: false,
  });
};
